import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Badge } from '../Badge/Badge';
import { Image } from '../Image/Image';
import { ArrowLink } from '../ArrowLink/ArrowLink';
import { Video } from '../Video/Video';
import { SectionHeader, hasSectionHeaderContent } from '../SectionHeader/SectionHeader';
import { hasCtaDestination } from '../../utils/ctaAction';
import type { CtaAction } from '../../utils/ctaAction';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import './CardGrid.scss';

/**
 * CardGrid - Card grid block with image-on-top or icon-on-top cards for Poly CMS
 *
 * Two media styles controlled by `mediaStyle`:
 * - `'image'` (default): Large banner image (2:1 ratio) edge-to-edge at the
 *   top of the card, with optional video play overlay.
 * - `'icon'`: 72px icon (48px on mobile) above the text, with standard card
 *   padding. Video is ignored in icon mode.
 *
 * Every card also supports:
 * - Optional badge pill
 * - Title + description
 * - Optional details text (date, read time, video duration)
 * - At most one CTA link; when present (and the card has no video), the whole
 *   card becomes the click target
 * - Hover lift effect with shadow
 *
 * Supports 2, 3, or 4 columns, up to 6 cards (wrapping into rows).
 * Section header with optional badge, heading, subheading, and ArrowLink CTA.
 */

/** Per-card link fields */
interface CardLinkFields {
  text?: string;
  href?: string;
  external?: boolean;
  action?: CtaAction;
  popupTargetId?: string;
}

/** Assembled card data (internal) */
interface CardData {
  heading?: string;
  description?: string;
  imageUrl?: string;
  imageAlt?: string;
  videoUrl?: string;
  badgeText?: string;
  badgeColor?: string;
  detailsText?: string;
  link: CardLinkFields;
}

/** Per-card flat props for CMS (cards 1-6). Flat + annotatable (was a mapped type). */
export interface CardSlotProps {
  /** Card 1 */
  card1Heading?: string;
  card1Description?: string;
  /** @polyType Url */
  card1ImageUrl?: string;
  /** @polyHelp Describes the image. When the card has no heading, this is the card's name for screen readers, so it must identify the card (e.g. the provider or brand shown). */
  card1ImageAlt?: string;
  /** @polyType Url */
  card1VideoUrl?: string;
  card1BadgeText?: string;
  /** @polyTranslatable false */
  card1BadgeColor?: string;
  card1DetailsText?: string;
  /** @polyHelp CTA text — also the whole-card click target. Give each card distinct, action-specific text, unless the cards are image-only and told apart by their alt text. */
  card1Link1Text?: string;
  /** @polyType Url */
  card1Link1Href?: string;
  /** @polyDefault false */
  card1Link1External?: boolean;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  card1Link1Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  card1Link1TargetPopupId?: string;

  /** Card 2 */
  card2Heading?: string;
  card2Description?: string;
  /** @polyType Url */
  card2ImageUrl?: string;
  /** @polyHelp Describes the image. When the card has no heading, this is the card's name for screen readers, so it must identify the card (e.g. the provider or brand shown). */
  card2ImageAlt?: string;
  /** @polyType Url */
  card2VideoUrl?: string;
  card2BadgeText?: string;
  /** @polyTranslatable false */
  card2BadgeColor?: string;
  card2DetailsText?: string;
  /** @polyHelp CTA text — also the whole-card click target. Give each card distinct, action-specific text, unless the cards are image-only and told apart by their alt text. */
  card2Link1Text?: string;
  /** @polyType Url */
  card2Link1Href?: string;
  /** @polyDefault false */
  card2Link1External?: boolean;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  card2Link1Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  card2Link1TargetPopupId?: string;

  /** Card 3 */
  card3Heading?: string;
  card3Description?: string;
  /** @polyType Url */
  card3ImageUrl?: string;
  /** @polyHelp Describes the image. When the card has no heading, this is the card's name for screen readers, so it must identify the card (e.g. the provider or brand shown). */
  card3ImageAlt?: string;
  /** @polyType Url */
  card3VideoUrl?: string;
  card3BadgeText?: string;
  /** @polyTranslatable false */
  card3BadgeColor?: string;
  card3DetailsText?: string;
  /** @polyHelp CTA text — also the whole-card click target. Give each card distinct, action-specific text, unless the cards are image-only and told apart by their alt text. */
  card3Link1Text?: string;
  /** @polyType Url */
  card3Link1Href?: string;
  /** @polyDefault false */
  card3Link1External?: boolean;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  card3Link1Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  card3Link1TargetPopupId?: string;

  /** Card 4 */
  card4Heading?: string;
  card4Description?: string;
  /** @polyType Url */
  card4ImageUrl?: string;
  /** @polyHelp Describes the image. When the card has no heading, this is the card's name for screen readers, so it must identify the card (e.g. the provider or brand shown). */
  card4ImageAlt?: string;
  /** @polyType Url */
  card4VideoUrl?: string;
  card4BadgeText?: string;
  /** @polyTranslatable false */
  card4BadgeColor?: string;
  card4DetailsText?: string;
  /** @polyHelp CTA text — also the whole-card click target. Give each card distinct, action-specific text, unless the cards are image-only and told apart by their alt text. */
  card4Link1Text?: string;
  /** @polyType Url */
  card4Link1Href?: string;
  /** @polyDefault false */
  card4Link1External?: boolean;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  card4Link1Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  card4Link1TargetPopupId?: string;

  /** Card 5 */
  card5Heading?: string;
  card5Description?: string;
  /** @polyType Url */
  card5ImageUrl?: string;
  /** @polyHelp Describes the image. When the card has no heading, this is the card's name for screen readers, so it must identify the card (e.g. the provider or brand shown). */
  card5ImageAlt?: string;
  /** @polyType Url */
  card5VideoUrl?: string;
  card5BadgeText?: string;
  /** @polyTranslatable false */
  card5BadgeColor?: string;
  card5DetailsText?: string;
  /** @polyHelp CTA text — also the whole-card click target. Give each card distinct, action-specific text, unless the cards are image-only and told apart by their alt text. */
  card5Link1Text?: string;
  /** @polyType Url */
  card5Link1Href?: string;
  /** @polyDefault false */
  card5Link1External?: boolean;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  card5Link1Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  card5Link1TargetPopupId?: string;

  /** Card 6 */
  card6Heading?: string;
  card6Description?: string;
  /** @polyType Url */
  card6ImageUrl?: string;
  /** @polyHelp Describes the image. When the card has no heading, this is the card's name for screen readers, so it must identify the card (e.g. the provider or brand shown). */
  card6ImageAlt?: string;
  /** @polyType Url */
  card6VideoUrl?: string;
  card6BadgeText?: string;
  /** @polyTranslatable false */
  card6BadgeColor?: string;
  card6DetailsText?: string;
  /** @polyHelp CTA text — also the whole-card click target. Give each card distinct, action-specific text, unless the cards are image-only and told apart by their alt text. */
  card6Link1Text?: string;
  /** @polyType Url */
  card6Link1Href?: string;
  /** @polyDefault false */
  card6Link1External?: boolean;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  card6Link1Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  card6Link1TargetPopupId?: string;
}

/**
 * @polySemanticUsage About this block: a grid of visually distinct, self-contained cards — each
 * with its own image, title, description, and optional CTA. Use it for showcasing programs,
 * product categories, resources, or any collection of items that benefit from visual separation
 * and individual click-through.
 * Image-only cards (logo or partner directories): a card needs a heading, a description, or an
 * image to render; title and description are optional. When a card has no heading, set
 * card{N}ImageAlt to the name of what the card represents (the provider, brand, or product), and
 * take that name from the source image alt, not from a button label. Repeating the same CTA text
 * on every card ("Contact provider") is expected there: the alt is added to each link's
 * screen-reader text, so that is what tells the cards apart.
 * Image-only cards must use mediaStyle image: the icon media style hides the image from screen
 * readers, which would leave a heading-less card with no accessible name at all.
 * CardGrid vs ContentGrid — scan ALL items in the source section as one unit: if any
 * single item carries multiple text-link CTAs, the entire section is a ContentGrid; use CardGrid
 * only when every card has exactly one CTA and the whole card is the click target. CardGrid's
 * intent is a substantive overview within cards; ContentGrid's is featured education with
 * explicit text CTAs as the interactive elements. If the scan is ambiguous, choose the closest
 * fit and record the ambiguity in your migration self-evaluation notes — never as visible text
 * in any content field.
 * CardGrid vs LinkCollection: LinkCollection is compact, utilitarian wayfinding
 * (title + short description + optional icon; no images, no editorial depth, no CTA buttons)
 * whose intent is navigation to tools, guides, and resources. CardGrid carries substantive
 * content depth within each card. Both are fully clickable — decide by depth and intent.
 * Single-card pattern: columns=1 with a single card is a valid, supported way to
 * express one standalone card; the card renders horizontally (media beside content). This is
 * the preferred mapping for "video cards" and other single media+text moments that visually
 * read as a card — prefer it over SplitContent in those cases.
 * Cards support video: set card{N}VideoUrl (with card{N}ImageUrl as the poster)
 * to render a click-to-play lightbox video in the card's media slot. Video renders only when
 * mediaStyle is image (the default) — with mediaStyle icon the card's video is silently
 * suppressed, so never pair card{N}VideoUrl with the icon media style.
 */
export interface CardGridProps extends React.HTMLAttributes<HTMLElement>, CardSlotProps {
  /**
   * Number of columns in the grid layout.
   * When 1, the card renders horizontally (image left, content right).
   * At 4 columns the grid steps down to 2-up in the tablet-landscape range
   * before collapsing to 1-up on tablet/mobile.
   * @polyType Number
   */
  columns?: 1 | 2 | 3 | 4;

  /**
   * How the card's media is rendered.
   * - `'image'` (default): full-width banner image at the top of the card.
   * - `'icon'`: 72px centered icon above the text (video is ignored).
   * @polyLabel Media Style
   * @polyOptions Image=image,Icon=icon
   */
  mediaStyle?: 'image' | 'icon';

  /** Section-level badge text (e.g. "New Seller Incentives") */
  sectionBadgeText?: string;
  /** Section heading */
  sectionHeading?: string;
  /** Section subheading */
  sectionSubheading?: string;
  /**
   * Section CTA button label (rendered next to the heading)
   * @polyHelp Keep it to 2 to 5 words (about 25 characters, 40 at most). Translations can run longer, and long labels wrap onto extra lines.
   */
  sectionCtaText?: string;
  /**
   * Section CTA button href
   * @polyType Url
   */
  sectionCtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  sectionCtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  sectionCtaTargetPopupId?: string;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

/**
 * Extract card data from flat CMS props for a given slot (1-6).
 *
 * A slot counts as occupied when it carries a heading, a description, or an
 * image. Gating on the heading alone would silently drop image-only cards,
 * which migrated content depends on for logo grids where each card is a
 * picture plus a CTA and the name lives only in the image alt text.
 */
function getCard(props: CardSlotProps, slot: 1 | 2 | 3 | 4 | 5 | 6): CardData | null {
  const heading = props[`card${slot}Heading`];
  const description = props[`card${slot}Description`];
  const imageUrl = props[`card${slot}ImageUrl`];
  if (!heading && !description && !imageUrl) return null;
  return {
    heading,
    description,
    imageUrl,
    imageAlt: props[`card${slot}ImageAlt`],
    videoUrl: props[`card${slot}VideoUrl`],
    badgeText: props[`card${slot}BadgeText`],
    badgeColor: props[`card${slot}BadgeColor`],
    detailsText: props[`card${slot}DetailsText`],
    link: {
      text: props[`card${slot}Link1Text`],
      href: props[`card${slot}Link1Href`],
      external: props[`card${slot}Link1External`],
      action: props[`card${slot}Link1Action`],
      popupTargetId: props[`card${slot}Link1TargetPopupId`],
    },
  };
}

/** Pattern to identify card slot props (card1*, card2*, etc.) */
const cardPropPattern = /^card[1-6]/;

/**
 * Render the card's single CTA link. `hiddenName` is appended as visually
 * hidden text so a card with no heading still has a distinct link name.
 */
function renderLink(link: CardLinkFields, hiddenName?: string) {
  if (!link.text || !hasCtaDestination(link.href, link.action, link.popupTargetId)) return null;
  const isExternal = (!link.action || link.action === 'link') && link.external;
  return (
    <div className="card-grid__links">
      <ArrowLink
        href={link.href}
        action={link.action}
        popupTargetId={link.popupTargetId}
        direction={isExternal ? 'diagonal' : 'right'}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
      >
        {link.text}
        {hiddenName && (
          <span className="poly-visually-hidden">, {hiddenName}</span>
        )}
        {isExternal && (
          <span className="poly-visually-hidden">(opens in new tab)</span>
        )}
      </ArrowLink>
    </div>
  );
}

export const CardGrid: React.FC<CardGridProps> = ({
  columns = 3,
  mediaStyle = 'image',
  sectionBadgeText,
  sectionHeading,
  sectionSubheading,
  sectionCtaText,
  sectionCtaHref,
  sectionCtaAction,
  sectionCtaTargetPopupId,
  anchorId,
  ...rest
}) => {
  const {
    className: extraClassName,
    ...mixedProps
  } = rest as React.HTMLAttributes<HTMLElement> & CardSlotProps;

  const containerProps = Object.fromEntries(
    Object.entries(mixedProps).filter(([key]) => !cardPropPattern.test(key))
  );

  const cards: CardData[] = ([1, 2, 3, 4, 5, 6] as const)
    .map(slot => getCard(rest as CardSlotProps, slot))
    .filter((c): c is CardData => c !== null);

  const slotProps = rest as CardSlotProps;
  const unnamedImageCardSlots = ([1, 2, 3, 4, 5, 6] as const)
    .filter(slot => slotProps[`card${slot}ImageUrl`] && !slotProps[`card${slot}Heading`] && !slotProps[`card${slot}ImageAlt`])
    .join(', ');

  React.useEffect(() => {
    if (!unnamedImageCardSlots) return;
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console -- author guidance during local dev
      console.warn(
        `[CardGrid] Card slot(s) ${unnamedImageCardSlots} have an image but no heading and no image alt text, so the card has no accessible name. Set card{N}ImageAlt to the name of what the card represents.`
      );
    }
  }, [unnamedImageCardSlots]);

  const hasSectionHeader = hasSectionHeaderContent({
    heading: sectionHeading,
    badge: sectionBadgeText,
    subheading: sectionSubheading,
    ctaText: sectionCtaText,
    ctaHref: sectionCtaHref,
    ctaAction: sectionCtaAction,
    ctaTargetPopupId: sectionCtaTargetPopupId,
  });

  const colClass = ` card-grid--${columns}col`;
  const mediaClass = mediaStyle === 'icon' ? ' card-grid--icon' : ' card-grid--image';
  const headlessClass = hasSectionHeader ? '' : ' card-grid--headless';

  return (
    <section
      {...containerProps}
      {...blockAnchorAttributes(anchorId)}
      className={`card-grid${colClass}${mediaClass}${headlessClass} ${extraClassName || ''}`}
    >
      <div className="card-grid__container">
        <SectionHeader
          badge={sectionBadgeText}
          heading={sectionHeading}
          subheading={sectionSubheading}
          ctaText={sectionCtaText}
          ctaHref={sectionCtaHref}
          ctaAction={sectionCtaAction}
          ctaTargetPopupId={sectionCtaTargetPopupId}
          className="card-grid__section-header"
        />

        <div className="card-grid__grid">
          {cards.map((card, index) => {
            // A video card keeps the lightbox button as its click target —
            // stretching the link over it would nest two interactive targets.
            const hasLink = Boolean(
              card.link.text && hasCtaDestination(card.link.href, card.link.action, card.link.popupTargetId),
            );
            const isClickable = hasLink && !card.videoUrl;
            // On a heading-less card the alt text is added to the link's name, so the
            // image is marked decorative to keep screen readers from reading the name twice.
            const linkCarriesName = !card.heading && Boolean(card.imageAlt && hasLink);
            return (
            <article
              key={`card-${index}`}
              className={`card-grid__card${isClickable ? ' card-grid__card--clickable' : ''}`}
            >
              {card.imageUrl && (
                mediaStyle === 'icon' ? (
                  <div className="card-grid__icon" aria-hidden="true">
                    <Image url={card.imageUrl} alt={card.imageAlt ?? card.heading ?? ''} objectFit="contain" />
                  </div>
                ) : card.videoUrl ? (
                  <Video
                    posterUrl={card.imageUrl}
                    posterAlt={card.imageAlt ?? card.heading ?? ''}
                    videoUrl={card.videoUrl}
                    playBehavior="lightbox"
                    className="card-grid__image"
                  />
                ) : (
                  <div className="card-grid__image">
                    <Image url={card.imageUrl} alt={linkCarriesName ? '' : (card.imageAlt ?? card.heading ?? '')} objectFit="cover" />
                  </div>
                )
              )}

              <div className="card-grid__content">
                {card.badgeText && (
                  <Badge
                    size="sm"
                    tone="dark"
                    className="card-grid__card-badge"
                    style={card.badgeColor ? { backgroundColor: card.badgeColor } : undefined}
                  >
                    {card.badgeText}
                  </Badge>
                )}

                {(card.heading || card.description) && (
                  <div className="card-grid__text">
                    {card.heading && <h3 className="card-grid__title">{card.heading}</h3>}
                    {card.description && (
                      <div className="card-grid__description">
                        <ReactMarkdown
                          components={{ p: ({ children }) => <>{children}</> }}
                          allowedElements={['p', 'a', 'strong', 'em', 'code']}
                        >
                          {card.description}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                )}

                {card.detailsText && (
                  <p className="card-grid__details">{card.detailsText}</p>
                )}

                {renderLink(card.link, linkCarriesName ? card.imageAlt : undefined)}
              </div>
            </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
